import { Menu, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../features/auth/AuthProvider';
import { useHealth } from '../features/system/HealthProvider';
import { systemDetailsPath, systemStateView } from '../features/system/systemState';
import { useTimeFormat } from '../lib/useTimeFormat';
import { UserMenu } from './UserMenu';

interface Props {
  onOpenCommand: () => void;
  /** Opens the navigation drawer (phones only). */
  onOpenMenu: () => void;
  menuOpen: boolean;
}

export function Topbar({ onOpenCommand, onOpenMenu, menuOpen }: Props) {
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-2 sm:gap-3 sm:px-4">
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label="Abrir menu"
        aria-haspopup="dialog"
        aria-expanded={menuOpen}
        aria-controls={menuOpen ? 'mobile-menu' : undefined}
        className="grid size-10 shrink-0 place-items-center rounded-md text-fg-muted hover:bg-surface-hover hover:text-fg md:hidden"
      >
        <Menu aria-hidden="true" className="size-5" strokeWidth={1.75} />
      </button>
      <button
        type="button"
        onClick={onOpenCommand}
        aria-label="Buscar ou ir para…"
        className="flex size-10 shrink-0 items-center justify-center gap-2 rounded-md text-fg-subtle transition-colors hover:text-fg-muted sm:h-8 sm:w-full sm:max-w-md sm:shrink sm:justify-start sm:border sm:border-line sm:bg-canvas sm:px-2.5 sm:text-[13px] sm:hover:border-line-strong"
      >
        <Search aria-hidden="true" className="size-4 sm:size-3.5" strokeWidth={2} />
        <span aria-hidden="true" className="hidden flex-1 text-left sm:inline">
          Buscar ou ir para…
        </span>
        <kbd
          aria-hidden="true"
          className="hidden rounded border border-line bg-surface-raised px-1.5 font-mono text-2xs text-fg-subtle md:inline"
        >
          Ctrl K
        </kbd>
      </button>
      <div className="ml-auto flex items-center gap-2 sm:gap-4">
        <SystemStatusIndicator />
        <Clock />
        <UserMenu />
      </div>
    </header>
  );
}

/**
 * Readiness for everyone (public health endpoint); only super administrators,
 * who hold system.read, follow it to the details on the "Painel" (0.67.0).
 */
function SystemStatusIndicator() {
  const { state } = useHealth();
  const { can } = useAuth();
  const view = systemStateView[state];
  const label = `Estado do sistema: ${view.label}`;
  const badge = (
    <StatusBadge tone={view.tone} pulse={state === 'checking'} labelClassName="hidden sm:inline">
      {view.label}
    </StatusBadge>
  );
  if (!can('system.read')) {
    // The badge hides its text on phones: the label is read from the
    // sr-only text, announced when the state changes.
    return (
      <span role="status" aria-label={label} title={label}>
        <span className="sr-only">{label}</span>
        <span aria-hidden="true">{badge}</span>
      </span>
    );
  }
  return (
    <Link
      to={systemDetailsPath}
      aria-label={label}
      title={label}
      className="rounded-full focus-visible:outline-offset-1"
    >
      {badge}
    </Link>
  );
}

/** Isolated so its per-second re-render never touches the rest of the shell. */
function Clock() {
  const fmt = useTimeFormat();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, []);
  return (
    <time
      dateTime={now.toISOString()}
      className="hidden font-mono text-xs text-fg-muted tabular-nums sm:block"
    >
      {fmt.clock(now)}
    </time>
  );
}
